import { Component, type ErrorInfo, type ReactNode } from 'react';
import { withTranslation, type WithTranslation } from 'react-i18next';
import { Box, Button, Typography, Paper } from '@mui/material';

interface Props extends WithTranslation {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// Wraps the entire app; shows a full-page fallback on uncaught render errors.
class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught an error', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      const { t } = this.props;
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            bgcolor: 'background.default',
          }}
        >
          <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center' }} elevation={0} variant="outlined">
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
              {t('errorBoundary.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('errorBoundary.message')}
            </Typography>
            <Button variant="contained" onClick={() => window.location.reload()}>
              {t('errorBoundary.refresh')}
            </Button>
          </Paper>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default withTranslation()(ErrorBoundary);
